import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Gift, Copy, CheckCheck } from 'lucide-react';

const PRESET_AMOUNTS = [20, 50, 100, 150, 200];

export default function AdminGiftCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [formData, setFormData] = useState({
    initial_amount: 50,
    custom_amount: '',
    use_custom: false,
    recipient_name: '',
    recipient_email: '',
    message: ''
  });

  const fetchCards = useCallback(async () => {
    try {
      const res = await api.get('/admin/gift-cards');
      setCards(res.data || []);
    } catch (err) {
      const status = err.response?.status;
      if (status !== 401 && status !== 403) toast.error('Erreur chargement');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('tapisstore_admin_token')) fetchCards();
    else setLoading(false);
  }, [fetchCards]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const amount = formData.use_custom ? parseFloat(formData.custom_amount) : formData.initial_amount;
      if (!amount || amount <= 0) { toast.error('Montant invalide'); setSaving(false); return; }
      await api.post('/admin/gift-cards', {
        initial_amount: amount,
        recipient_name: formData.recipient_name || null,
        recipient_email: formData.recipient_email || null,
        message: formData.message || null
      });
      toast.success('Bon cadeau généré !');
      setDialogOpen(false);
      setFormData({ initial_amount: 50, custom_amount: '', use_custom: false, recipient_name: '', recipient_email: '', message: '' });
      fetchCards();
    } catch (err) {
      const status = err.response?.status;
      if (status !== 401 && status !== 403) toast.error('Erreur lors de la création');
    } finally { setSaving(false); }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Supprimer le bon "${code}" ?`)) return;
    try {
      await api.delete(`/admin/gift-cards/${code}`);
      toast.success('Bon supprimé');
      fetchCards();
    } catch (err) {
      const status = err.response?.status;
      if (status !== 401 && status !== 403) toast.error('Erreur lors de la suppression');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  const usedCount = cards.filter(c => c.used).length;
  const totalValue = cards.reduce((s, c) => s + c.initial_amount, 0);

  return (
    <AdminLayout>
      <div data-testid="admin-gift-cards">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold">Bons cadeaux</h1>
            <p className="text-muted-foreground mt-1">Générez et gérez vos bons cadeaux Villa Marceau</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-gift-card-btn">
                <Plus className="w-4 h-4 mr-2" />
                Générer un bon
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Gift className="w-5 h-5 text-primary" />
                  Nouveau bon cadeau
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-2">
                <div>
                  <Label className="mb-2 block">Montant</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {PRESET_AMOUNTS.map(a => (
                      <button
                        type="button"
                        key={a}
                        onClick={() => setFormData(p => ({ ...p, initial_amount: a, use_custom: false }))}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                          !formData.use_custom && formData.initial_amount === a
                            ? 'bg-primary text-white border-primary'
                            : 'border-border hover:bg-muted'
                        }`}
                      >
                        {a} €
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setFormData(p => ({ ...p, use_custom: true }))}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        formData.use_custom ? 'bg-primary text-white border-primary' : 'border-border hover:bg-muted'
                      }`}
                    >
                      Autre
                    </button>
                  </div>
                  {formData.use_custom && (
                    <Input
                      type="number"
                      min="1"
                      step="0.01"
                      value={formData.custom_amount}
                      onChange={e => setFormData(p => ({ ...p, custom_amount: e.target.value }))}
                      placeholder="Montant en €"
                      className="mt-1"
                    />
                  )}
                </div>
                <div>
                  <Label htmlFor="recipient_name">Destinataire (nom)</Label>
                  <Input id="recipient_name" value={formData.recipient_name} onChange={e => setFormData(p => ({ ...p, recipient_name: e.target.value }))} placeholder="Marie Dupont" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="recipient_email">Email destinataire (optionnel)</Label>
                  <Input id="recipient_email" type="email" value={formData.recipient_email} onChange={e => setFormData(p => ({ ...p, recipient_email: e.target.value }))} placeholder="marie@example.com" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="gift_message">Message personnel (optionnel)</Label>
                  <Textarea id="gift_message" value={formData.message} onChange={e => setFormData(p => ({ ...p, message: e.target.value }))} placeholder="Joyeux anniversaire !" className="mt-1 min-h-[80px]" />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                  <Button type="submit" className="btn-primary" disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
                    Générer
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Total bons', value: cards.length },
            { label: 'Utilisés', value: usedCount },
            { label: 'Valeur totale émise', value: `${totalValue.toFixed(2)} €` }
          ].map(s => (
            <Card key={s.label} className="border border-border">
              <CardContent className="p-4">
                <p className="text-2xl font-bold font-heading">{s.value}</p>
                <p className="text-sm text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Table */}
        {cards.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Gift className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Aucun bon cadeau généré</p>
          </div>
        ) : (
          <div className="space-y-3">
            {cards.map(card => (
              <Card key={card.code} className="border border-border">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                      <Gift className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-base tracking-wider">{card.code}</span>
                        <button onClick={() => copyCode(card.code)} className="text-muted-foreground hover:text-foreground transition-colors" title="Copier le code">
                          {copiedCode === card.code ? <CheckCheck className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {card.recipient_name && <span>{card.recipient_name} — </span>}
                        Émis le {new Date(card.created_at).toLocaleDateString('fr-FR')}
                      </p>
                      {card.message && <p className="text-xs italic text-muted-foreground mt-0.5">"{card.message}"</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="font-semibold">{card.remaining_amount?.toFixed(2)} €</p>
                      {card.remaining_amount !== card.initial_amount && (
                        <p className="text-xs text-muted-foreground">sur {card.initial_amount.toFixed(2)} €</p>
                      )}
                    </div>
                    <Badge variant={card.used ? 'destructive' : 'default'} className={card.used ? '' : 'bg-green-100 text-green-800 border-green-200'}>
                      {card.used ? 'Utilisé' : 'Valide'}
                    </Badge>
                    <button onClick={() => handleDelete(card.code)} className="text-muted-foreground hover:text-destructive transition-colors p-1" title="Supprimer">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
